import { Theme, Typography } from "@mui/material";
import Chip, { ChipProps } from "@mui/material/Chip";
import styled from "@mui/material/styles/styled";

import type { MeetingReportTier } from "@app/analytics/MeetingReport/types";

import { Row } from "@rui/Stack";
import TooltipIcon from "@rui/TooltipIcon";

interface TierChipProps {
  className?: ChipProps["className"];
  tier: Exclude<MeetingReportTier, "unknown">;
  tooltip?: string;
}

const LABEL = {
  full: "Premium report",
  lite: "Restricted data",
  deleted: "Data deleted",
  out_of_quota: "Out of quota",
  notetaker: "Notetaker report",
  uploaded: "Uploaded report",
};

const getStyleForTier = (tier: TierChipProps["tier"], theme: Theme) => {
  switch (tier) {
    case "full":
      return {
        background: theme.palette.gradients.read,
        color: "#FFF",
      };
    case "lite":
      return {
        backgroundColor: theme.palette.shades.negative[50],
        color: theme.palette.error.main,
      };
    case "out_of_quota": {
      return {
        backgroundColor: theme.palette.shades.orange[50],
        color: "#CC8634",
      };
    }
    case "deleted":
      return {
        backgroundColor: theme.palette.shades.neutral[50],
        color: theme.palette.shades.neutral[500],
      };
    case "notetaker":
      return {
        backgroundColor: "#E29201",
        color: "#FFF",
      };
    case "uploaded":
      return {
        background: theme.palette.gradients.blueGreen,
        color: "#fff",
      };
    default:
      return {};
  }
};

const TierChip = styled(({ tier, tooltip, ...props }: TierChipProps) => (
  <Chip
    label={
      <Row variant="left" spacing={0.5}>
        <Typography variant="labelMed">{LABEL[tier]}</Typography>
        {tooltip && (
          <TooltipIcon title={tooltip} iconColor="inherit" height={12} />
        )}
      </Row>
    }
    {...props}
  />
))(({ theme, tier }) => ({
  letterSpacing: "-0.01rem",
  height: "fit-content",
  ...getStyleForTier(tier, theme),
  "& .MuiChip-label": {
    padding: "2px 8px",
  },
}));

export default TierChip;
